import React, { FC, ReactNode, useMemo, useState } from 'react'
import styled from 'styled-components'

import { createTestId } from 'aem-libs-tests/src/utility/createTestId'

import { ConditionalRender } from '../../utility/conditional-render'
import ArrowIco from './../../../assets/arrow.svg'
import * as InfoBoxStyled from './InfoBox.styled'

type InfoType = (string | ReactNode)[]

const switchDuration = 500

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const ArrowButton = styled.button<{ $position: 'left' | 'right' }>`
  border: none;
  background: url(${ArrowIco}) no-repeat center center;
  width: 20px;
  height: 20px;
  transform: rotate(${({ $position }) => ($position === 'left' ? '90deg' : '-90deg')});
`

const useInfoTip = (tips: InfoType) => {
  const [tipIndex, setTipIndex] = useState(0)

  const showTipByIndex = (index: number) => {
    setTimeout(() => {
      setTipIndex(index)
    }, switchDuration)
  }

  const nextInfo = () => {
    const plusIndex = tipIndex + 1
    showTipByIndex(plusIndex > tips.length - 1 ? 0 : plusIndex)
  }

  const previousInfo = () => {
    const minusIndex = tipIndex - 1
    showTipByIndex(minusIndex < 0 ? tips.length - 1 : minusIndex)
  }

  const currentInfo = useMemo(() => {
    const tipsList = Array.isArray(tips) ? [...tips] : [tips]

    return tipsList.at(tipIndex)
  }, [tips, tipIndex])

  return {
    currentInfo,
    nextInfo,
    previousInfo
  }
}

export interface InfoBoxProps {
  info?: string | string[] | ReactNode | ReactNode[]
  children?: ReactNode
  infoPrefix?: ReactNode | string
  icon?: string
  testId?: string
}

export const InfoBox: FC<InfoBoxProps> = ({ children, info, icon, testId, infoPrefix = <strong>TIP: </strong> }) => {
  const { currentInfo, nextInfo, previousInfo } = useInfoTip(Array.isArray(info) ? info : [info ?? children])

  const usedTestId = testId ?? 'info-box'

  return (
    <Container>
      <ConditionalRender
        predicate={Array.isArray(info)}
        element={
          <ArrowButton
            data-testid={createTestId(usedTestId, 'left-button')}
            $position={'left'}
            onClick={previousInfo}
            aria-label={'Tip předchozí'}
          />
        }
      />
      <InfoBoxStyled.Container $icon={icon}>
        <InfoBoxStyled.Message data-testid={createTestId(usedTestId, 'message')}>
          {infoPrefix}
          {currentInfo}
        </InfoBoxStyled.Message>
      </InfoBoxStyled.Container>
      <ConditionalRender
        predicate={Array.isArray(info)}
        element={
          <ArrowButton
            data-testid={createTestId(usedTestId, 'right-button')}
            $position={'right'}
            onClick={nextInfo}
            aria-label={'Tip další'}
          />
        }
      />
    </Container>
  )
}
